'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {observer} from "mobx-react";

import Button from 'react-bootstrap/Button';
import Alert from "react-bootstrap/Alert";

import {FaStar, FaRegStar, FaTimes, FaCheck, FaExclamationCircle} from "react-icons/fa";

import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {isFalse, makeUniqueId, makeId} from '@uw-it-sis/lib-react/lib/Utils'
import UnknownState from '@uw-it-sis/lib-react/lib/UnknownState';
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {MetricKeys, MetricLabels} from './Constants';

import BookmarkWidgetStore from './BookmarkWidgetStore'
import PreferredCampusStore from "./PreferredCampusStore";

const analyticsStore = appStore.getAnalyticsStore();

/**
 * This is a widget which will add or delete a bookmark for a plan item.
 *
 * !!! IMPORTANT TO UNDERSTAND !!!
 *
 * This store and the BookmarksStore handles bookmarks for both courses and programs.
 *
 * The code property is totally confusing. In the database the refObjectId for CreditCourse bookmarks code is the version
 * independent id of the course. For program bookmarks, however, code is in fact the program code.
 *
 * To create a CreditCourse the bookmark API expects the course code and not the version independent id. It looks up the
 * version independent id on the backend. The resulting bookmark comes back with its code property set to the
 * version independent id. However, if you call getBookmarks from the API the response has the code property of
 * CreditCourses set to the course code. So, the bookmark model from addBookmark and the [bookmark, bookmark] returned
 * from getBookmarks are inconsistent. That should be resolved, but I'm not sure how. Having the course code is very
 * handy in the UI code. Having the model represent what's actually stored has its own appeal.
 *
 * Anyway, leaving it as-is for now, but FIXME.
 */
class BookmarkWidget extends Component {
    /**
     * Note: For course bookmarks the 'code' property is actually the id and not the course code.
     * For programs, however, 'code' should be the program code.
     */
    constructor(props) {
        super(props);
        this.props = props;
        this.store = new BookmarkWidgetStore();
        this.handleClick = this.handleClick.bind(this);
        this.handleLoginClick = this.handleLoginClick.bind(this);
    }

    componentDidMount() {
        this.store.mount();
    }

    /**
     * Handler for login clicks.
     */
    handleLoginClick = () => appStore.doLogin();

    /**
     * Callback for when the button is clicked.
     */
    handleClick = () => {
        const pageContext = this.props.analyticsContext;
        const preferredCampus = PreferredCampusStore.campus;
        const code = this.props.code;

        /**
         * If the plan item has been bookmarked then delete on click. Otherwise, add on click.
         */
        if (this.store.isBookmarked(this.props.code)) {
            if (pageContext) {
                const labels = analyticsStore.label(MetricLabels.campus, preferredCampus).add(MetricLabels.programCode, code).get();
                analyticsStore.addEvent(`browse-${pageContext}-unbookmarked`, labels, {});
            }
            // Remove the bookmark.
            let bookmark = this.store.getBookmarkByCode(this.props.code);
            this.store.delete(bookmark.id);
        } else {
            if (pageContext) {
                const labels = analyticsStore.label(MetricLabels.campus, preferredCampus).add(MetricLabels.programCode, code).get();
                analyticsStore.addEvent(`browse-${pageContext}-bookmarked`, labels, {});
            }
            // Add the bookmark.
            this.store.add(this.props.code, this.props.type);
        }
    };

    makeBookmarksWidget() {
        // Return a sign-in link if not authenticated
        if (! appStore.isAuthenticated() ) {
            let button;
            let buttonId = `sign-in-bookmark-${makeUniqueId()}`;
            if ( this.props.component === 'button' ) {
                button =
                    <Button id={buttonId}
                            variant="primary"
                            onClick={this.handleLoginClick}>
                            Sign in to Add Bookmark
                    </Button>;
            } else {
                button =
                    <Button id={buttonId}
                            variant="inline-link"
                            className="text-muted"
                            title="Sign in to Add Bookmark"
                            onClick={this.handleLoginClick}>
                        <FaRegStar aria-hidden="true" className="fs-5" />
                    </Button>;
            }
            return button;
        }

        /*
         * This code is a little messy because we need to sometimes render the bookmarks widget as a button,
         * sometimes as a toggling star, and sometimes a non-toggling X. Long term the button requirement
         * should go away and we'll only be left with the start vs X, so I'm just going to make it work rather
         * than trying to clean it up.
         */
        let IconType = FaStar;
        let iconColor = "text-gold";
        let buttonText = "Remove Bookmark";
        let isToggleMode = true;

        // If toggle behavior is false then this is meant to be an remove/X button.
        if ( isFalse(this.props.toggle) ) {
            IconType = FaTimes;
            iconColor = "text-body";
            isToggleMode = false;
        }

        let checkedStatus = this.store.isBookmarked(this.props.code);
        if ( ! checkedStatus && isToggleMode ) {
            IconType = FaRegStar;
            iconColor = "text-body";
            buttonText = "Add Bookmark";
        }

        let component;
        let componentAttr = {
            'id': `bookmark-${makeId(this.props.code)}-${makeUniqueId()}`,
            'data-code': this.props.code,
            'data-type': this.props.type
        };
        switch (this.store.state) {
            case States.pending:
                // Put in a placeholder button so the widgets don't jump around if an operation is pending.
                if (this.props.component === 'button' ) {
                    // Text button that will be replaced by a feedback component after action taken
                    // No click handler on this button ...
                    component =
                        <Button {...componentAttr}
                                variant="secondary">
                            <IconType className="me-2" aria-hidden="true"/>
                            {buttonText}
                        </Button>;
                } else {
                    component =
                        <Button {...componentAttr}
                                variant="inline-link"
                                className={iconColor}
                                title={buttonText}
                                aria-label={buttonText}
                                aria-pressed={checkedStatus}>
                            <IconType aria-hidden="true" className="fs-5"/>
                        </Button>;
                }
                break;
            case States.error:
                component =
                    <Alert variant="danger" className="py-2">
                        <FaExclamationCircle className="me-2" aria-hidden="true"/>
                        Operation Failed
                    </Alert>;
                break;
            case States.initial:
                if (this.props.component === 'button') {
                    // Text button that will be replaced by a feedback component after action taken
                    component =
                        <Button {...componentAttr}
                                variant="secondary"
                                className="d-flex align-items-center"
                                onClick={this.handleClick}>
                            <IconType className="me-2" aria-hidden="true"/>
                            {buttonText}
                        </Button>;
                } else {
                    // Toggle button to add/remove bookmark
                    if (appStore.isAdviser()) {
                        // If Adviser, just show current bookmark state
                        component =
                            <div className={iconColor}
                                 title={checkedStatus ? 'Bookmarked' : 'Not Bookmarked'}>
                                <IconType aria-hidden="true" className="fs-5"/>
                            </div>;
                    } else {
                        component =
                            <Button {...componentAttr}
                                    variant="inline-link"
                                    onClick={this.handleClick}
                                    className={iconColor}
                                    title={buttonText}
                                    aria-label={buttonText}
                                    aria-pressed={checkedStatus}>
                                <IconType aria-hidden="true" className="fs-5"/>
                            </Button>;
                    }
                }
                break;
            case States.done:
                if (this.props.showSuccess) {
                    // Show the success feedback widget if applicable.
                    component =
                        <Alert variant="success" className="py-2">
                            <FaCheck className="me-2" aria-hidden="true"/>
                            {this.store.actionMessage}
                        </Alert>;
                } else {
                    // Otherwise, toggle button to add/remove bookmark

                    if (appStore.isAdviser()) {
                        // If Adviser, just show current bookmark state
                        component =
                            <div className={iconColor}
                                 title={checkedStatus ? 'Bookmarked' : 'Not Bookmarked'}>
                                <IconType aria-hidden="true" className="fs-5"/>
                            </div>;
                    } else {
                        component =
                            <Button {...componentAttr}
                                    variant="inline-link"
                                    onClick={this.handleClick}
                                    className={iconColor}
                                    title={buttonText}
                                    aria-label={buttonText}
                                    aria-pressed={checkedStatus}>
                                <IconType aria-hidden="true" className="fs-5"/>
                            </Button>;
                    }
                }
                break;
            default:
                component = <UnknownState name="BookmarkWidget"/> ;
        }

        return component;
    }

    render() {
        return this.makeBookmarksWidget();
    }
}

/**
 * !!! See the class doc for important info about the code property.
 *
 * code: The course or bookmark code
 * type: The type of bookmark. Values: kuali.lu.type.CreditCourse, uw.academicplan.program)
 * component: The type of component to render. Value: button or empty.
 * analyticsContext: The "page" on which the component is being rendered.
 * toggle: Whether the widget should having toggling behavior. Seeing this to true is how you get the X.
 *    toggle is required in order to avoid ambiguity of what leaving toggle as undefined means.
 * showSuccess: Whether or not to show a success/error indicator.
 *
 */
BookmarkWidget.propTypes = {
    code: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    component: PropTypes.string,
    analyticsContext: PropTypes.string,
    toggle: PropTypes.bool.isRequired,
    showSuccess: PropTypes.bool
};

export default observer(BookmarkWidget);
